import { useCallback, useRef } from "react";
import type { Dialog } from "@/types/dialog";
import { getLanguageConfig } from "@/lib/language-config";
import { generateAudio } from "@/lib/content-api";
import { useDialogStore } from "./dialog-store";

const audioUrlCache = new Map<string, string>();

declare global {
  interface Window {
    google: { translate: { TranslateElement: new (opts: Record<string, unknown>, id: string) => unknown } };
    googleTranslateInit: () => void;
  }
}

interface AudioPlayerProps {
  dialog: Dialog;
  onOpenRoleplay: () => void;
}

export function AudioPlayer({ dialog, onOpenRoleplay }: AudioPlayerProps) {
  const toggles = useDialogStore((s) => s.toggles);
  const toggle = useDialogStore((s) => s.toggle);
  const gtInitialized = useRef(false);
  const audio = useDialogStore((s) => s.audio);
  const setAudio = useDialogStore((s) => s.setAudio);
  const textSizeLevel = useDialogStore((s) => s.textSizeLevel);
  const setTextSizeLevel = useDialogStore((s) => s.setTextSizeLevel);
  const config = getLanguageConfig(dialog.language_code);

  function handlePlayAll() {
    if (audio.isPlayingAll) {
      stopPlayback();
    } else {
      setAudio({ isPlayingAll: true, currentLineIndex: 0 });
      playFromIndex(0);
    }
  }

  function playFromIndex(index: number) {
    if (index >= dialog.lines.length) {
      const state = useDialogStore.getState();
      if (state.audio.repeatMode === "all") {
        playFromIndex(0);
        return;
      }
      stopPlayback();
      return;
    }

    const line = dialog.lines[index];
    const cacheKey = `${dialog.id}|${index}`;
    const cachedUrl = audioUrlCache.get(cacheKey);
    const audioUrl = line?.audioUrl ?? cachedUrl ?? null;

    if (!audioUrl) {
      setAudio({ currentLineIndex: index });
      const speaker = line ? dialog.speakers[line.speakerIndex] : null;
      if (speaker?.fonVoiceId && line?.text) {
        setAudio({ loadingLine: index });
        generateAudio(speaker.fonVoiceId, line.text, dialog.id, dialog.language_code, index)
          .then((result) => {
            audioUrlCache.set(cacheKey, result.audio_url);
            playAudioUrl(result.audio_url, index);
          })
          .catch(() => {
            setAudio({ loadingLine: null });
            setTimeout(() => playFromIndex(index + 1), 2000);
          });
      } else {
        setTimeout(() => playFromIndex(index + 1), 2000);
      }
      return;
    }

    setAudio({ currentLineIndex: index });
    playAudioUrl(audioUrl, index);
  }

  function playAudioUrl(url: string, index: number) {
    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (!el) { stopPlayback(); return; }

    setAudio({ loadingLine: null });
    el.src = url;
    el.playbackRate = useDialogStore.getState().audio.playbackSpeed;
    el.onended = () => {
      const state = useDialogStore.getState();
      if (state.audio.repeatMode === "line") {
        el.currentTime = 0;
        el.play().catch(() => {});
      } else if (state.audio.isPlayingAll) {
        playFromIndex(index + 1);
      }
    };
    el.play().catch(() => {});
  }

  function stopPlayback() {
    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (el) {
      el.pause();
      el.currentTime = 0;
      el.onended = null;
    }
    setAudio({ isPlayingAll: false, isPlaying: false, loadingLine: null });
  }

  function playLineAudio(lineIndex: number) {
    const line = dialog.lines.find((l) => l.index === lineIndex);
    if (!line) return;

    const cacheKey = `${dialog.id}|${lineIndex}`;
    const url = line.audioUrl ?? audioUrlCache.get(cacheKey) ?? null;

    if (url) {
      playAudioUrl(url, lineIndex);
      return;
    }

    const speaker = dialog.speakers[line.speakerIndex];
    if (!speaker?.fonVoiceId || !line.text) return;

    setAudio({ loadingLine: lineIndex });
    generateAudio(speaker.fonVoiceId, line.text, dialog.id, dialog.language_code, lineIndex)
      .then((result) => {
        audioUrlCache.set(cacheKey, result.audio_url);
        playAudioUrl(result.audio_url, lineIndex);
      })
      .catch(() => setAudio({ loadingLine: null }));
  }

  const initGoogleTranslate = useCallback(() => {
    if (gtInitialized.current) return;
    gtInitialized.current = true;

    const container = document.createElement("div");
    container.id = "gt-container";
    container.style.cssText =
      "position:fixed;top:70px;right:20px;z-index:9999;background:#1e293b;border:1px solid #334155;border-radius:12px;padding:12px 16px;box-shadow:0 10px 40px rgba(0,0,0,0.4);";
    container.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">' +
      '<span style="font-size:12px;color:#94a3b8;">Translate page</span>' +
      '<button id="gt-close" style="background:none;border:none;color:#64748b;font-size:16px;cursor:pointer;padding:0 0 0 12px;line-height:1;">✕</button>' +
      '</div>' +
      '<div id="google_translate_element"></div>';
    document.body.appendChild(container);

    document.getElementById("gt-close")?.addEventListener("click", () => {
      container.style.display = "none";
      useDialogStore.getState().toggle("translation");
    });

    window.googleTranslateInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "auto",
          includedLanguages: "en,zh-TW,zh-CN,ja,ko,es,fr,de,ru,pt,ar,vi,th,id,tr,pl,nl,it",
          layout: 1,
          autoDisplay: false,
        },
        "google_translate_element",
      );
    };

    const script = document.createElement("script");
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateInit";
    document.body.appendChild(script);
  }, []);

  function handleTranslateToggle() {
    const next = !toggles.translation;
    toggle("translation");
    const container = document.getElementById("gt-container");
    if (next) {
      if (!gtInitialized.current) {
        initGoogleTranslate();
      } else if (container) {
        container.style.display = "block";
      }
    } else {
      if (container) container.style.display = "none";
      const frame = document.querySelector(".goog-te-banner-frame") as HTMLIFrameElement | null;
      if (frame) frame.style.display = "none";
      // Clear Google Translate cookie and restore original text
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + location.hostname;
      const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
      if (combo) {
        combo.value = "";
        combo.dispatchEvent(new Event("change"));
      }
      // Google Translate restores text on page reload after cookie clear
      const banner = document.getElementById(":1.container");
      if (banner) banner.remove();
      document.querySelectorAll(".translated-ltr,.translated-rtl").forEach((el) => {
        el.classList.remove("translated-ltr", "translated-rtl");
      });
    }
  }

  const toggleBtn = (active: boolean) =>
    active
      ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-glossika-600 text-white transition-colors"
      : "px-3 py-1.5 rounded-lg text-xs font-medium bg-th-input border border-th-border text-th-text-2 hover:bg-th-btn hover:border-th-text-3 transition-colors";

  return (
    <>
      <div className="text-center mb-4">
        <button
          className="px-6 py-3 bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-pink-500/25"
          onClick={onOpenRoleplay}
        >
          Role-Play Mode
        </button>
      </div>

      <div className="bg-th-surface-dim rounded-xl border border-th-border p-5 mb-6 shadow-xl sticky top-16 z-40 backdrop-blur-sm">
        <div className="text-xs text-th-text-2 font-medium mb-3">Audio Playback</div>

        <div className="flex items-center gap-4 flex-wrap">
          <button
            className="w-12 h-12 rounded-full bg-glossika-600 hover:bg-glossika-500 text-white text-lg flex items-center justify-center transition-colors shadow-lg shadow-glossika-500/25 flex-shrink-0"
            onClick={handlePlayAll}
          >
            {audio.isPlayingAll ? "⏹" : "▶"}
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Play All</div>
            <div className="text-xs text-th-text-2">
              {audio.isPlayingAll
                ? `Line ${audio.currentLineIndex + 1} / ${dialog.lines.length}`
                : "Ready"}
            </div>
          </div>
          <div className="flex gap-1">
            <button
              className="w-8 h-8 bg-th-input border border-th-border rounded-lg text-th-text-2 text-xs font-bold hover:bg-th-btn transition-colors"
              onClick={() => setTextSizeLevel(textSizeLevel - 1)}
              title="Smaller text"
            >
              A
            </button>
            <button
              className="w-8 h-8 bg-th-input border border-th-border rounded-lg text-th-text-2 text-base font-bold hover:bg-th-btn transition-colors"
              onClick={() => setTextSizeLevel(textSizeLevel + 1)}
              title="Larger text"
            >
              A
            </button>
          </div>
        </div>

        <div className="flex gap-2 mt-4 flex-wrap items-center">
          <span className="text-xs text-th-text-2 mr-1">Speed:</span>
          <select
            className="bg-slate-800 border border-slate-600 rounded-lg px-2.5 py-1.5 text-xs text-slate-200"
            value={audio.playbackSpeed}
            onChange={(e) => {
              const speed = parseFloat(e.target.value);
              setAudio({ playbackSpeed: speed });
              const el = document.getElementById("audio-element") as HTMLAudioElement | null;
              if (el) el.playbackRate = speed;
            }}
          >
            <option value="0.5" className="bg-slate-800 text-slate-200">0.5x</option>
            <option value="0.75" className="bg-slate-800 text-slate-200">0.75x</option>
            <option value="1" className="bg-slate-800 text-slate-200">1x</option>
            <option value="1.25" className="bg-slate-800 text-slate-200">1.25x</option>
            <option value="1.5" className="bg-slate-800 text-slate-200">1.5x</option>
            <option value="1.75" className="bg-slate-800 text-slate-200">1.75x</option>
            <option value="2" className="bg-slate-800 text-slate-200">2x</option>
          </select>
          <span className="text-xs text-th-text-2 ml-3 mr-1">Repeat:</span>
          {(["off", "line", "all"] as const).map((mode) => (
            <button
              key={mode}
              className={toggleBtn(audio.repeatMode === mode)}
              onClick={() => setAudio({ repeatMode: mode })}
            >
              {mode === "off" ? "Off" : mode === "line" ? "Line" : "All"}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mt-3 flex-wrap items-center">
          <button className={toggleBtn(toggles.transcription)} onClick={() => toggle("transcription")}>
            Transcription
          </button>
          {config.hasNastaliq && (
            <button className={toggleBtn(toggles.nastaliq)} onClick={() => toggle("nastaliq")}>
              Nastaliq
            </button>
          )}
          {config.hasVocalization && (
            <button className={toggleBtn(toggles.vocalization)} onClick={() => toggle("vocalization")}>
              Vocalization
            </button>
          )}
          <button className={toggleBtn(toggles.translation)} onClick={handleTranslateToggle}>
            Translate
          </button>
          <div className="flex-1" />
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-th-input border border-th-border text-th-text-2 hover:text-th-text hover:bg-th-btn transition-colors"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Top
          </button>
        </div>
      </div>
    </>
  );
}
